/**
 * Impersonation — master vê o sistema "como" um cliente, sem perder seus
 * privilégios.
 *
 * Implementação:
 *   - localStorage guarda o tenantId que o master quer impersonar
 *   - useAuth lê esse localStorage e, SE for master, sobrescreve o `tenantId`
 *     da sessão pra esse valor + seta `impersonating`
 *   - AppLayout mostra um banner amarelo no topo enquanto está ativo
 *   - Botão "Sair" limpa o localStorage e recarrega
 *
 * Limitação: regras Firestore precisam permitir o master acessar dados
 * de qualquer tenant. Isso já está garantido em rules.firestore
 * (`isOfTenant(tid) := isMaster() || userTenant() == tid`).
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/config/firebase'

const KEY = 'eventpro_impersonate_tid'
const KEY_NAME = 'eventpro_impersonate_name'

export function setImpersonation(tenantId: string, displayName: string) {
  try {
    localStorage.setItem(KEY, tenantId)
    localStorage.setItem(KEY_NAME, displayName)
    void writeAudit('impersonation.start', tenantId, { displayName })
  } catch { /* localStorage indisponível */ }
}

export function getImpersonationTid(): string | null {
  try {
    return localStorage.getItem(KEY) || null
  } catch {
    return null
  }
}

export function getImpersonationName(): string | null {
  try {
    return localStorage.getItem(KEY_NAME) || null
  } catch {
    return null
  }
}

export function clearImpersonation() {
  try {
    const tid = localStorage.getItem(KEY)
    localStorage.removeItem(KEY)
    localStorage.removeItem(KEY_NAME)
    if (tid) void writeAudit('impersonation.end', tid, {})
  } catch { /* localStorage indisponível */ }
}

/** Grava entrada em audit_logs/. Best-effort — não falha se Firestore offline. */
async function writeAudit(event: string, tenantId: string, metadata: Record<string, unknown>) {
  try {
    const user = auth.currentUser
    if (!user) return
    await addDoc(collection(db, 'audit_logs'), {
      ts: serverTimestamp(),
      tenantId,
      actor: {
        uid: user.uid,
        email: user.email || '',
        role: 'master',
        impersonating: tenantId,
      },
      event,
      severity: event.startsWith('impersonation') ? 'warning' : 'info',
      metadata,
    })
  } catch { /* offline — perde o log */ }
}

export function isImpersonating(): boolean {
  return !!getImpersonationTid()
}
